import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useB3Lang } from '@b3/lang';
import { Box } from '@mui/material';

import { B2BAutoCompleteCheckbox } from '@/components';
import B3Filter from '@/components/filter/B3Filter';
import B3Spin from '@/components/spin/B3Spin';
import { B3PaginationTable } from '@/components/table/B3PaginationTable';
import { TableColumnItem } from '@/components/table/B3Table';
import { useMobile, useSort } from '@/hooks';
import OrderCancellationStatus from '@/pages/order/components/OrderCancellationStatus';
import OrderStatus from '@/pages/order/components/OrderStatus';
import { OrderItemCard } from '@/pages/order/OrderItemCard';
import { ORDER_STATUS } from '@/pages/order/shared/getOrderStatus';
import { getCompanyData, getOrdersCreatedByUser } from '@/shared/service/b2b';
import { getSalesOrders } from '@/shared/service/request/alumio';
import { isB2BUserSelector, useAppSelector } from '@/store';
import { CustomerRole } from '@/types';
import { displayFormat, formatCurrency } from '@/utils';

import { ORDER_CANCELLATION_STATUS } from './shared/getOrderCancellationStatus';
import {
  defaultSortKey,
  FilterSearchProps,
  getFilterMoreData,
  getInitFilter,
  sortKeys,
} from './config';

interface CompanyInfoProps {
  companyId: string;
  companyName: string;
  companyAddress: string;
  companyCountry: string;
  companyState: string;
  companyCity: string;
  companyZipCode: string;
  phoneNumber: string;
  bcId: string;
}

interface ListItem {
  firstName: string;
  lastName: string;
  id: string;
  orderId: string;
  poNumber?: string;
  money: string;
  totalIncTax: string;
  status: string;
  statusCode?: string;
  cancellationStatus: string;
  cancellationStatusCode?: string;
  createdAt: string;
  companyName: string;
  companyInfo?: CompanyInfoProps;
  AccountParty?: any;
  Status?: any;
  PriceAndTaxCalculation?: any;
  ItemListCancellationStatusName?: any;
  MainTotal?: any;
  PostingDate?: any;
  LifeCycleStatusCodeText?: any;
  BuyerParty?: any;
  ProductRecipientParty?: any;
  PostingDateTime?: any;
  SalesUnitParty?: any;
  LifeCycleStatusCode?: any;
  InvoiceStatusCodeText?: any;
  GrossAmount?: any;
  GrossAmountCurrencyCode?: any;
  DocumentReference?: any;
  CreationDateTime?: any;
  orderNumber: string;
  orderTotal: {
    amount: number;
  };
  company: string;
}

interface SearchChangeProps {
  startValue?: string;
  endValue?: string;
  PlacedBy?: string;
  orderStatus?: string | number;
  orderCancellationStatus?: string | number;
  company?: string;
  id: number;
  orderId?: string;
}

interface OrderProps {
  isCompanyOrder?: boolean;
}

function useData() {
  const isB2BUser = useAppSelector(isB2BUserSelector);
  const companyB2BId = useAppSelector(({ company }) => company.companyInfo.id);
  const role = useAppSelector(({ company }) => company.customer.role);
  const salesRepCompanyId = useAppSelector(({ b2bFeatures }) => b2bFeatures.masqueradeCompany.id);
  const isAgenting = useAppSelector(({ b2bFeatures }) => b2bFeatures.masqueradeCompany.isAgenting);

  const { order: orderSubViewPermission } = useAppSelector(
    ({ company }) => company.pagesSubsidiariesPermission,
  );

  const { selectCompanyHierarchyId, isEnabledCompanyHierarchy } = useAppSelector(
    ({ company }) => company.companyHierarchyInfo,
  );
  const currentCompanyId =
    role === CustomerRole.SUPER_ADMIN && isAgenting
      ? Number(salesRepCompanyId)
      : Number(companyB2BId);

  const companyId = companyB2BId || salesRepCompanyId;

  return {
    role,
    isAgenting,
    isB2BUser,
    orderSubViewPermission,
    selectCompanyHierarchyId,
    isEnabledCompanyHierarchy,
    currentCompanyId,
    companyId,
  };
}

function Order({ isCompanyOrder = false }: OrderProps) {
  const hasAppliedUrlFilter = useRef(false);
  const b3Lang = useB3Lang();
  const [isMobile] = useMobile();
  const {
    role,
    isAgenting,
    companyId,
    isB2BUser,
    orderSubViewPermission,
    selectCompanyHierarchyId,
    isEnabledCompanyHierarchy,
    currentCompanyId,
  } = useData();

  const [isRequestLoading, setIsRequestLoading] = useState(false);
  const [allTotal, setAllTotal] = useState(0);
  const [filterData, setFilterData] = useState<Partial<FilterSearchProps> | null>(null);
  const [filterInfo, setFilterInfo] = useState<Array<any>>([]);
  const [getOrderStatuses, setOrderStatuses] = useState<Array<any>>([]);
  const [isAutoRefresh, setIsAutoRefresh] = useState(false);

  const [handleSetOrderBy, order, orderBy] = useSort(
    sortKeys,
    defaultSortKey,
    filterData,
    setFilterData,
  );

  const EXCLUDED_STATUS_CODES = ['4'];
  const EXCLUDED_CANCELLATION_CODES: string[] = [];

  interface ExtraField {
    fieldName: string;
    fieldValue: string;
  }

  useEffect(() => {
    const search = getInitFilter(isCompanyOrder, isB2BUser);
    if (isB2BUser) {
      search.companyIds = [Number(selectCompanyHierarchyId) || Number(currentCompanyId)];
    }
    setFilterData(search);
    setIsAutoRefresh(true);

    if (role === 100) return;

    const initFilter = async () => {
      const createdByUsers =
        isB2BUser && isCompanyOrder ? await getOrdersCreatedByUser(Number(companyId), 0) : {};

      const orderStatuses = Object.values(ORDER_STATUS)
        .filter((status) => !EXCLUDED_STATUS_CODES.includes(String(status.code)))
        .map((status) => ({
          systemLabel: status.label,
          customLabel: b3Lang(status.translationKey),
          statusCode: String(status.code),
        }));

      const cancellationStatusFilterOptions = Object.values(ORDER_CANCELLATION_STATUS)
        .filter((status) => !EXCLUDED_CANCELLATION_CODES.includes(String(status.code)))
        .map((status) => ({
          systemLabel: status.label,
          customLabel: b3Lang(status.translationKey),
          statusCode: String(status.code),
        }));

      // This removes the "Open (1)" and "unknown (0)" statuses from the filter options
      const filterInfo = getFilterMoreData(
        isB2BUser,
        role,
        isCompanyOrder,
        isAgenting,
        createdByUsers,
        orderStatuses,
        cancellationStatusFilterOptions,
      );
      setOrderStatuses(orderStatuses);

      const filterInfoWithTranslatedLabel = filterInfo.map((element) => {
        const translatedElement = element;
        translatedElement.label = b3Lang(element.idLang);

        if (element.name === 'orderStatus') {
          translatedElement.options = (element.options || []).map((option) => {
            if (option.systemLabel != null) {
              const status = ORDER_STATUS[option.systemLabel];
              if (status) {
                option.customLabel = b3Lang(status.translationKey);
              }
            }
            return option;
          });
        }

        return element;
      });

      setFilterInfo(filterInfoWithTranslatedLabel);
    };

    initFilter();
    // disabling as we only need to run this once and values at starting render are good enough
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectCompanyHierarchyId]);

  const fetchOrders = useCallback(
    async (
      params: Partial<FilterSearchProps>,
    ): Promise<{ edges: ListItem[]; totalCount: number }> => {
      let data: ListItem[] = [];
      let totalCount = 0;

      if (!hasAppliedUrlFilter.current) {
        const hash = window.location.hash || '';
        const [path, query] = hash.split('?');
        const searchParams = new URLSearchParams(query);
        const orderIdFromUrl = searchParams.get('orderId') || '';

        if (orderIdFromUrl) {
          params.orderId = orderIdFromUrl;

          setFilterData((prev) => ({
            ...prev,
            orderId: orderIdFromUrl,
          }));

          setTimeout(() => {
            const filterTriggerEvent = new CustomEvent('b3-filter-applied', {
              detail: {
                orderId: orderIdFromUrl,
              },
            });
            window.dispatchEvent(filterTriggerEvent);
          }, 0);

          searchParams.delete('orderId');
          const newHash = searchParams.toString() ? `${path}?${searchParams.toString()}` : path;
          window.location.hash = newHash;
        }

        hasAppliedUrlFilter.current = true;
      }

      try {
        const resCompany = await getCompanyData(companyId);
        const accountId = resCompany?.data?.extraFields.find(
          (field: ExtraField) => field.fieldName === 'account_id',
        )?.fieldValue;

        if (!accountId) {
          console.error('Account ID not found in extraFields.');
          return { edges: [], totalCount: 0 };
        }

        const response = await getSalesOrders(accountId, params);

        console.log('response:', response);
        if (response) {
          const { count, orders = [] } = response;
          totalCount = count;
          data = orders;
        }
        console.log('Fetched sales orders:', data, 'Total count:', totalCount);
      } catch (e) {
        console.error('Failed to fetch orders:', e);
      }

      setAllTotal(totalCount);
      setIsAutoRefresh(false);
      return {
        edges: data,
        totalCount,
      };
    },
    [companyId],
  );

  const navigate = useNavigate();

  const goToDetail = (item: ListItem, index: number) => {
    navigate(`/orderDetail/${item.id}`, {
      state: {
        currentIndex: index,
        searchParams: filterData,
        totalCount: allTotal,
        isCompanyOrder,
        beginDateAt: filterData?.beginDateAt,
        endDateAt: filterData?.endDateAt,
      },
    });
  };

  const columnAllItems: TableColumnItem<ListItem>[] = [
    {
      key: 'id',
      title: b3Lang('orders.order'),
      width: '10%',
      isSortable: true,
    },
    {
      key: 'invoiceIds',
      title: b3Lang('orders.invoice'),
      width: '8%',
      isSortable: false,
      render: (item: any) => {
        const invoiceIds = item?.documentReferences?.invoiceIds;

        if (!invoiceIds || invoiceIds.length === 0) return '-';

        return (
          <Box>
            {invoiceIds.map((id: string, index: number) => (
              <Box
                key={id}
                sx={{
                  color: '#000000',
                  cursor: 'pointer',
                  ':hover': {
                    textDecoration: 'underline',
                  },
                  mb: index !== invoiceIds.length - 1 ? 1 : 0,
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(`/invoice?invoiceId=${id}`);
                }}
              >
                {id}
              </Box>
            ))}
          </Box>
        );
      },
    },
    {
      key: 'deliveryId',
      title: b3Lang('orders.delivery'),
      width: '8%',
      isSortable: false,
      render: (item: any) => {
        const deliveryIds = item?.documentReferences?.deliveryIds;

        if (!deliveryIds || deliveryIds.length === 0) return '-';

        return (
          <Box>
            {deliveryIds.map((id: string) => (
              <Box
                key={id}
                sx={{
                  color: '#000000',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  ':hover': {
                    textDecoration: 'underline',
                  },
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(`/deliveryDetail/${id}`);
                }}
              >
                {id}
              </Box>
            ))}
          </Box>
        );
      },
    },

    {
      key: 'companyName',
      title: b3Lang('orders.company'),
      width: '15%',
      isSortable: false,
      render: (item: any) => {
        return <Box>{item.company}</Box>;
      },
    },
    {
      key: 'placedBy',
      title: b3Lang('orders.placedBy'),
      render: (item: any) => `${item.buyer || '–'}`,
      width: '20%',
      isSortable: false,
    },
    {
      key: 'externalReference',
      title: b3Lang('orders.externalReference'),
      render: (item: any) => item.externalReference || '–',
      width: '20%',
      isSortable: false,
    },
    {
      key: 'createdAt',
      title: b3Lang('orders.createdOn'),
      render: (item: any) => `${displayFormat(item.createdAt, true)}`,
      width: '10%',
      isSortable: true,
    },
    {
      key: 'status',
      title: b3Lang('orders.orderStatus'),
      render: (item: any) => {
        return <OrderStatus text={item.status} code={item.statusCode} />;
      },
      width: '10%',
      isSortable: true,
    },
    {
      key: 'cancellationStatus',
      title: b3Lang('orders.cancellationStatus'),
      render: (item: any) => {
        return (
          <OrderCancellationStatus
            text={item.cancellationStatus}
            code={item.cancellationStatusCode}
          />
        );
      },
      width: '10%',
      isSortable: true,
    },
    {
      key: 'totalIncTax',
      title: b3Lang('orders.grandTotal'),
      render: (item: any) => formatCurrency(item.orderTotal.amount, item.orderTotal.currency),
      width: '8%',

      isSortable: true,
    },
  ];

  const getColumnItems = () => {
    const getNewColumnItems = columnAllItems.filter((item: { key: string }) => {
      const { key } = item;
      if (!isB2BUser && key === 'companyName') return false;
      if ((!isB2BUser || (Number(role) === 3 && !isAgenting)) && key === 'placedBy') return false;
      if (key === 'companyId' && isB2BUser && (Number(role) !== 3 || isAgenting)) return false;
      if (
        (key === 'companyId' || key === 'placedBy') &&
        !(Number(role) === 3 && !isAgenting) &&
        !isCompanyOrder
      )
        return false;
      return true;
    });

    return getNewColumnItems;
  };

  const handleChange = (key: string, value: string) => {
    if (key === 'search') {
      setFilterData({
        ...filterData,
        q: value,
      });
    }
  };

  const handleFilterChange = (value: SearchChangeProps) => {
    let currentStatus = value?.orderStatus || '';
    if (currentStatus) {
      const originStatus = getOrderStatuses.find(
        (status) =>
          status.customLabel === currentStatus ||
          status.systemLabel === currentStatus ||
          status.statusCode === currentStatus,
      );

      currentStatus = originStatus?.statusCode || null;
    }

    const search: Partial<FilterSearchProps> = {
      beginDateAt: value?.startValue || null,
      endDateAt: value?.endValue || null,
      createdBy: value?.PlacedBy || '',
      statusCode: currentStatus,
      orderCancellationStatusCode: value?.orderCancellationStatus || null,
      companyName: value?.company || '',
      orderId: value?.orderId,
    };
    setFilterData({
      ...filterData,
      ...search,
    });
  };

  const columnItems = getColumnItems();

  const handleSelectCompanies = (company: number[]) => {
    const newCompanyIds = company.includes(-1) ? [] : company;

    setFilterData({
      ...filterData,
      companyIds: newCompanyIds,
    });
  };

  const memoizedSearchParams = useMemo(() => filterData || {}, [filterData]);

  return (
    <B3Spin isSpinning={isRequestLoading}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: isMobile ? '100%' : 'auto',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: isMobile ? 'flex-start' : 'center',
            flexDirection: isMobile ? 'column' : 'row',

            '& > div': {
              width: isMobile ? '100%' : 'auto',
            },
          }}
        >
          {isEnabledCompanyHierarchy && orderSubViewPermission && (
            <Box sx={{ mr: isMobile ? 0 : '10px', mb: '30px' }}>
              <B2BAutoCompleteCheckbox handleChangeCompanyIds={handleSelectCompanies} />
            </Box>
          )}
          <B3Filter
            startPicker={{
              isEnabled: true,
              label: b3Lang('orders.from'),
              defaultValue: filterData?.beginDateAt || null,
              pickerKey: 'start',
            }}
            endPicker={{
              isEnabled: true,
              label: b3Lang('orders.to'),
              defaultValue: filterData?.endDateAt || null,
              pickerKey: 'end',
            }}
            filterMoreInfo={filterInfo}
            handleChange={handleChange}
            handleFilterChange={handleFilterChange}
            pcTotalWidth="100%"
            pcContainerWidth="100%"
            pcSearchContainerWidth="100%"
          />
        </Box>

        <B3PaginationTable
          columnItems={columnItems}
          rowsPerPageOptions={[10, 20, 30]}
          getRequestList={fetchOrders}
          searchParams={memoizedSearchParams}
          isCustomRender={false}
          requestLoading={setIsRequestLoading}
          tableKey="id"
          pageType="orderListPage"
          isAutoRefresh={isAutoRefresh}
          sortDirection={order}
          orderBy={orderBy}
          sortByFn={handleSetOrderBy}
          renderItem={(row: ListItem, index?: number) => (
            <OrderItemCard
              key={Number(row.id)}
              item={row}
              index={index}
              allTotal={allTotal}
              filterData={filterData}
              isCompanyOrder={isCompanyOrder}
            />
          )}
          onClickRow={(item: ListItem, index?: number) => {
            if (index !== undefined) {
              goToDetail(item, index);
            }
          }}
          hover
        />
      </Box>
    </B3Spin>
  );
}

export default Order;
